<div class="min-h-screen bg-slate-50 -m-4 sm:-m-8">

  @if (data()?.loading) {
    <div class="absolute inset-0 z-40 flex items-center justify-center bg-slate-50/70 backdrop-blur-[1px]"
         role="status" aria-live="polite" aria-busy="true">
      <div class="flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white px-8 py-6 shadow-lg">
        <i class="pi pi-spin pi-spinner text-3xl text-teal-600"></i>
        <p class="text-sm font-semibold text-slate-800">Cargando panorama operativo…</p>
      </div>
    </div>
  }

  <!-- Header -->
  <div class="bg-white border-b border-slate-200 px-6 py-5">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <div class="flex items-center gap-2">
          <span class="flex items-center justify-center w-9 h-9 rounded-xl bg-teal-600">
            <i class="pi pi-th-large text-white text-base"></i>
          </span>
          <div>
            <h1 class="text-2xl font-bold text-slate-900">Panorama operativo</h1>
            <p class="text-xs text-slate-500">Administración de ventas — {{ updatedLabel() }}</p>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <span class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-medium">
          <i class="pi pi-calendar text-slate-400"></i>
          {{ periodLabel() }}
        </span>

        <div class="flex rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm"
             [class.pointer-events-none]="data()?.loading"
             [class.opacity-60]="data()?.loading">
          @for (opt of periodOptions; track opt.value) {
            <button type="button" (click)="setPeriod(opt.value)"
              [disabled]="data()?.loading"
              class="px-3 py-2 text-xs font-semibold transition-colors disabled:cursor-wait"
              [class.bg-teal-600]="period().type === opt.value && opt.value !== 'custom'"
              [class.text-white]="period().type === opt.value && opt.value !== 'custom'"
              [class.text-slate-600]="period().type !== opt.value"
              [class.hover:bg-slate-50]="period().type !== opt.value && !data()?.loading">
              {{ opt.label }}
            </button>
          }
        </div>

        <button type="button" (click)="load()" [disabled]="data()?.loading"
          class="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 text-xs font-medium disabled:opacity-50 shadow-sm">
          <i class="pi pi-refresh text-sm" [class.animate-spin]="data()?.loading"></i>
          Actualizar
        </button>

        <a [routerLink]="['/sales/nuevo']"
          class="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white text-xs font-semibold hover:bg-teal-700 transition-colors shadow-sm">
          <i class="pi pi-plus"></i>
          Nueva venta
        </a>
      </div>
    </div>

    @if (period().type === 'custom') {
      <div class="mt-4 flex flex-wrap items-center gap-3 ml-11">
        <input type="date" [value]="customDateFrom()"
          (input)="customDateFrom.set($any($event.target).value)"
          class="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500">
        <span class="text-slate-400 text-sm">—</span>
        <input type="date" [value]="customDateTo()"
          (input)="customDateTo.set($any($event.target).value)"
          class="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500">
        <button type="button" (click)="applyCustomPeriod()"
          [disabled]="!customDateFrom() || !customDateTo() || !!data()?.loading"
          class="px-4 py-1.5 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50">
          Aplicar
        </button>
      </div>
    }
  </div>

  @if (data()?.error) {
    <div class="mx-6 mt-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3 text-sm flex items-center gap-2">
      <i class="pi pi-exclamation-triangle text-amber-500"></i>
      {{ data()?.error }}
    </div>
  }

  <div class="w-full p-6 space-y-6">

    <!-- Alerta vencidos -->
    @if (data()?.alert; as alert) {
      @if (alert.overdueCount > 0) {
        <div class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <div class="flex items-center gap-3 min-w-0">
            <span class="w-2 h-2 rounded-full bg-red-600 shrink-0"></span>
            <div class="text-sm text-slate-800">
              <strong>{{ alert.overdueCount }} recibos vencidos</strong>
              <span class="text-slate-500 ml-1">
                acumulan más de 5 días sin pago —
                {{ alert.overdueWithReminderCount }} ya tienen recordatorio enviado
              </span>
            </div>
          </div>
          <a [routerLink]="['/collections']"
            class="shrink-0 px-3 py-1.5 rounded-lg border border-red-300 bg-white text-red-700 text-xs font-semibold hover:bg-red-50">
            Revisar recibos vencidos →
          </a>
        </div>
      }
    }

    <!-- GRUPO: Recibos -->
    <section>
      <div class="flex items-center gap-2 mb-3">
        <span class="w-1.5 h-1.5 rounded-full bg-teal-600"></span>
        <h2 class="text-xs font-semibold uppercase tracking-wider text-slate-500">Recibos</h2>
        <div class="flex-1 h-px bg-slate-200"></div>
      </div>

      @if (data()?.receipts; as r) {
        <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div class="flex items-center justify-between text-xs text-slate-500 mb-2">
              <span>No. de recibos</span>
              <span class="px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 font-semibold">Total</span>
            </div>
            <p class="text-3xl font-bold tabular-nums text-slate-900">{{ r.total | number }}</p>
            @if (r.total > 0) {
              <div class="flex h-1.5 rounded-full overflow-hidden mt-3 bg-slate-100">
                <div class="bg-emerald-600" [style.width.%]="r.paidPercent"></div>
                <div class="bg-amber-500" [style.width.%]="r.pendingPercent"></div>
                <div class="bg-red-600" [style.width.%]="r.overduePercent"></div>
              </div>
            }
            <p class="text-[11px] text-slate-400 mt-2">Generados por suscripción y venta manual</p>
          </div>

          <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div class="flex items-center justify-between text-xs text-slate-500 mb-2">
              <span>Recibos cobrados</span>
              <span class="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">{{ r.paidPercent }}%</span>
            </div>
            <p class="text-3xl font-bold tabular-nums text-emerald-600">{{ r.paid | number }}</p>
            <p class="text-[11px] text-slate-400 mt-2">Conciliados en el periodo</p>
          </div>

          <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div class="flex items-center justify-between text-xs text-slate-500 mb-2">
              <span>Recibos pendientes</span>
              <span class="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold">{{ r.pendingPercent }}%</span>
            </div>
            <p class="text-3xl font-bold tabular-nums text-amber-600">{{ r.pending | number }}</p>
            <p class="text-[11px] text-slate-400 mt-2">Dentro de plazo de pago</p>
          </div>

          <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div class="flex items-center justify-between text-xs text-slate-500 mb-2">
              <span>Recibos vencidos</span>
              <span class="px-2 py-0.5 rounded-full bg-red-50 text-red-700 font-semibold">{{ r.overduePercent }}%</span>
            </div>
            <p class="text-3xl font-bold tabular-nums text-red-600">{{ r.overdue | number }}</p>
            <p class="text-[11px] text-slate-400 mt-2">Requieren gestión de cobranza</p>
          </div>
        </div>
      }
    </section>

    <!-- GRUPO: Ventas y facturación -->
    <section>
      <div class="flex items-center gap-2 mb-3">
        <span class="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
        <h2 class="text-xs font-semibold uppercase tracking-wider text-slate-500">Ventas y facturación</h2>
        <div class="flex-1 h-px bg-slate-200"></div>
      </div>

      @if (data()?.sales; as s) {
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div class="flex items-center justify-between text-xs text-slate-500 mb-2">
              <span>No. de ventas</span>
              <span class="px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 font-semibold">Periodo</span>
            </div>
            <p class="text-3xl font-bold tabular-nums text-slate-900">{{ s.total | number }}</p>
            <p class="text-[11px] text-slate-400 mt-2">
              {{ s.subscriptionCount | number }} por suscripción · {{ s.manualCount | number }} manuales
            </p>
          </div>

          <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div class="flex items-center justify-between text-xs text-slate-500 mb-2">
              <span>No. de facturadas</span>
              <span class="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">{{ s.invoicedPercent }}%</span>
            </div>
            <p class="text-3xl font-bold tabular-nums text-emerald-600">{{ s.invoiced | number }}</p>
            <p class="text-[11px] text-slate-400 mt-2">{{ s.pendingInvoice | number }} ventas pendientes de facturar</p>
          </div>
        </div>
      }
    </section>

    <!-- Visualizaciones -->
    <div class="grid grid-cols-1 xl:grid-cols-4 gap-4">
      <!-- Tendencia ventas vs facturadas (3/4) -->
      <div class="xl:col-span-3 bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <h3 class="text-sm font-semibold text-slate-800 mb-3">Ventas vs. facturadas (6 semanas)</h3>
        <div class="h-44 xl:h-52">
          @if ((data()?.salesTrend?.length ?? 0) > 0) {
            <p-chart type="line" [data]="trendData()" [options]="trendOptions" height="100%" />
          } @else {
            <p class="text-sm text-slate-400 py-8 text-center">Sin datos de tendencia</p>
          }
        </div>
      </div>

      <!-- Donut recibos (1/4) -->
      <div class="xl:col-span-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <h3 class="text-sm font-semibold text-slate-800 mb-3">Estatus de recibos</h3>
        @if (donutData(); as chart) {
          <div class="flex flex-col items-center gap-3">
            <div class="relative w-24 h-24 shrink-0">
              <p-chart type="doughnut" [data]="chart" [options]="donutOptions" height="96px" />
              <div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span class="text-sm font-bold tabular-nums">{{ data()?.receipts?.total | number }}</span>
                <span class="text-[8px] uppercase tracking-wide text-slate-400">recibos</span>
              </div>
            </div>
            <div class="w-full space-y-1.5 text-xs">
              <div class="flex justify-between"><span class="text-slate-500"><span class="inline-block w-2 h-2 rounded-sm bg-emerald-600 mr-1.5"></span>Cobrados</span><strong>{{ data()?.receipts?.paidPercent }}%</strong></div>
              <div class="flex justify-between"><span class="text-slate-500"><span class="inline-block w-2 h-2 rounded-sm bg-amber-500 mr-1.5"></span>Pendientes</span><strong>{{ data()?.receipts?.pendingPercent }}%</strong></div>
              <div class="flex justify-between"><span class="text-slate-500"><span class="inline-block w-2 h-2 rounded-sm bg-red-600 mr-1.5"></span>Vencidos</span><strong>{{ data()?.receipts?.overduePercent }}%</strong></div>
            </div>
          </div>
        } @else {
          <p class="text-sm text-slate-400 py-8 text-center">Sin recibos en el periodo</p>
        }
      </div>
    </div>

    <!-- Tabla + procesos -->
    <div class="grid grid-cols-1 xl:grid-cols-5 gap-4">
      <div class="xl:col-span-3 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 overflow-hidden">
        <h3 class="text-sm font-semibold text-slate-800 mb-3">Recibos vencidos recientes</h3>
        <p-table
          [value]="data()?.recentOverdue ?? []"
          [paginator]="true"
          [rows]="overduePageSize"
          [rowsPerPageOptions]="overduePageSizeOptions"
          [showCurrentPageReport]="true"
          currentPageReportTemplate="{first}–{last} de {totalRecords}"
          styleClass="p-datatable-sm sales-ops-overdue-table">
          <ng-template pTemplate="header">
            <tr>
              <th class="text-[11px] uppercase text-slate-400 font-medium">Recibo</th>
              <th class="text-[11px] uppercase text-slate-400 font-medium text-right">Total</th>
              <th class="text-[11px] uppercase text-slate-400 font-medium">Cliente</th>
              <th class="text-[11px] uppercase text-slate-400 font-medium hidden sm:table-cell">Origen</th>
              <th class="text-[11px] uppercase text-slate-400 font-medium text-right">Días</th>
              <th class="text-[11px] uppercase text-slate-400 font-medium">Estatus</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr
              [class.row-no-sale]="!row.saleId"
              (click)="openSaleFromRow(row)">
              <td class="text-xs font-medium tabular-nums">{{ row.chargeLabel }}</td>
              <td class="text-xs text-right tabular-nums font-semibold text-slate-800">
                {{ row.total | currency:'MXN':'symbol':'1.2-2' }}
              </td>
              <td class="text-xs">{{ row.customerName }}</td>
              <td class="text-xs hidden sm:table-cell text-slate-500">{{ row.sourceLabel }}</td>
              <td class="text-xs text-right tabular-nums font-semibold"
                  [class.text-red-600]="row.severity === 'overdue'"
                  [class.text-amber-600]="row.severity === 'due_soon'">
                {{ row.daysOverdue > 0 ? row.daysOverdue : '—' }}
              </td>
              <td>
                <p-tag [value]="row.statusLabel"
                  [severity]="row.severity === 'overdue' ? 'danger' : 'warn'"
                  styleClass="text-[10px]" />
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="6" class="text-center text-sm text-slate-400 py-6">No hay recibos vencidos recientes</td></tr>
          </ng-template>
        </p-table>
      </div>

      <!-- Estado de procesos automáticos -->
      <div class="xl:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <h3 class="text-sm font-semibold text-slate-800 mb-3">Estado de procesos automáticos</h3>
        <div class="space-y-0">
          @for (item of data()?.processes ?? []; track item.key) {
            <div class="flex gap-3 py-3 border-t border-slate-100 first:border-t-0 first:pt-0">
              <span class="w-2 h-2 rounded-full mt-1.5 shrink-0" [class]="processDotClass(item.tone)"></span>
              <div>
                <p class="text-xs font-semibold text-slate-800">{{ item.title }}</p>
                <p class="text-[11px] text-slate-500 mt-0.5">{{ item.description }}</p>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  </div>
</div>

<p-drawer
  [visible]="isSaleDrawerOpen()"
  position="right"
  (visibleChange)="onSaleDrawerVisibleChange($event)"
  styleClass="sale-details-drawer !w-[800px]"
  [blockScroll]="true">
  <ng-template pTemplate="header">
    <div class="flex items-center gap-3 min-w-0">
      <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-600 shadow-sm">
        <i class="pi pi-shopping-cart text-base"></i>
      </span>
      <h3 class="text-lg font-semibold text-slate-900">Detalle de venta</h3>
    </div>
  </ng-template>
  @if (saleDetailsLoading()) {
    <div class="flex flex-col items-center justify-center py-12 gap-4">
      <i class="pi pi-spin pi-spinner text-4xl text-sky-500"></i>
      <p class="text-sm text-slate-600">Cargando detalle...</p>
    </div>
  } @else if (selectedSale()) {
    <app-sale-read
      [sale]="selectedSale()!"
      [charges]="saleCharges()" />
  }
</p-drawer>
